import { centsToDollars, dollarsToCents, formatCents } from "@homero/pricing";
import Button from "antd/es/button";
import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src";
import { usePrice } from "src/hooks/usePrice";
import { useTasks } from "src/context/tasks";
import styled from "styled-components";
import { AnimatedCents } from "./cart-motion";
import { cartSummary } from "../../lib/price";
import { SAVINGS } from "./savings";
import { SummaryScrim, SummarySheet } from "./SummarySheet";

type BookingType = "booking" | "task_booking";

/** Seeds the per-instance ids above. Only ever incremented. */
let nextNavbarId = 0;
export default function MobileNavbar({
  nextAction,
  prevAction,
  nextText,
  prevText,
  loading,
  type = "booking",
  hidePrice = false,
  disabled = false,
}: {
  nextAction: () => void;
  prevAction: () => void;
  nextText: string;
  prevText: string;
  loading: boolean;
  type?: BookingType;
  hidePrice?: boolean;
  disabled?: boolean;
}) {
  const booking = useSelector((state: RootState) => state.bookingReducer);
  const { page } = booking;
  const { selectedTasks, taskPage } = useTasks();

  /*
    Subscribes the bar to the rates arriving from the API. The figures below
    are read straight out of `cartSummary` on every render rather than through
    this hook's getters, so keep it for the subscription alone.
  */
  usePrice();

  /**
   * One breakdown per render, unmemoised — the same call the cart and the
   * summary sheet make, so all three agree by construction.
   *
   * This used to be `useMemo(() => getSubtotal(), [type, selectedTasks,
   * getSubtotal])`, and `getSubtotal` is itself a `useCallback` keyed on the
   * redux slice. The pricing config lives in module state, not redux, so when
   * the API's rate card replaced the bundled fallback nothing in that
   * dependency list changed: `usePricingConfig` forced the re-render and the
   * memo handed back its cached figure anyway. The bar went on quoting
   * fallback prices for the rest of the session while every other surface
   * moved — invisible until the summary sheet put the live number directly
   * above the stale one and the two disagreed by twenty dollars.
   *
   * Recomputing costs one pass over a dozen integer fields, which is cheaper
   * than the render it sits in.
   */
  const bookingSummary = cartSummary(booking);

  const subTotal =
    type === "task_booking"
      ? selectedTasks.reduce(
          (sum, task) => sum + task.current_price_cents / 100,
          0
        )
      : centsToDollars(bookingSummary.breakdown.subTotalCents);

  const currentPage = type === "task_booking" ? taskPage : page;

  /**
   * Every saving on this booking, summed — the figure the green line under the
   * subtotal reports. Taken from `cartSummary` rather than added up here so a
   * new kind of discount reaches this line the day it is added; the bar was
   * previously the one surface a new saving would silently skip.
   *
   * Tasks price from their own list and have no booking discounts, so the line
   * is a booking-only thing.
   */
  const discountCents =
    type === "task_booking" ? 0 : bookingSummary.discountCents;

  /**
   * The summary is a booking idea: it renders the booking ladder out of
   * `bookingReducer`, which on a basket of tasks would quote the wrong thing
   * entirely. Same rule `App.tsx` applies to the sticky total.
   */
  const canOpenSummary = type === "booking" && !hidePrice;

  const [summaryOpen, setSummaryOpen] = useState(false);
  const closeSummary = useCallback(() => setSummaryOpen(false), []);

  /*
    A per-instance id pair for `aria-controls`. Hand-rolled rather than
    `useId`, which React 17 does not have — this app is on 17 (`package.json`),
    so importing it type-checks against @types/react but is undefined at
    runtime.
  */
  const idRef = useRef<number>();
  if (idRef.current === undefined) {
    nextNavbarId += 1;
    idRef.current = nextNavbarId;
  }
  const triggerId = `summary-trigger-${idRef.current}`;
  const sheetId = `summary-sheet-${idRef.current}`;

  /**
   * This bar's height is not a constant: it grows with the savings line and
   * with the phone's safe-area inset. Measured and published as a custom
   * property so the step layout can leave room for it at the bottom
   * (`.mobile-step-layout` in index.css).
   */
  const footerRef = useRef<HTMLElement>(null);
  React.useLayoutEffect(() => {
    const el = footerRef.current;
    if (!el) return undefined;
    const root = document.documentElement.style;
    let published = "";
    const publish = () => {
      const height = `${Math.round(el.getBoundingClientRect().height)}px`;
      if (height === published) return;
      published = height;
      root.setProperty("--homero-bar-h", height);
    };
    publish();
    /*
      Border box, not the default content box. The safe-area inset lives in the
      bar's bottom *padding*, and on iOS it switches between 0 and 34px as the
      browser toolbar hides and shows, which a content-box observer never
      sees. The resize listener stays as a backstop: the toolbar also resizes
      the window, and a padding-only change was not reported by the observer in
      Chrome's device emulation. Unchanged heights are not rewritten.

      Mount-only: the observer sees every height change the props cause.
      Removed on unmount so a page with another bar gets the stylesheet's
      fallback rather than this bar's last height.
    */
    window.addEventListener("resize", publish);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(el, { box: "border-box" });
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", publish);
      root.removeProperty("--homero-bar-h");
    };
  }, []);

  const priceBlock = (
    <Price>
      <PriceLabel>
        Subtotal{" "}
        {type === "task_booking"
          ? "(" + selectedTasks.length + " tasks)"
          : ""}
        {canOpenSummary ? <Caret $open={summaryOpen} aria-hidden="true" /> : null}
      </PriceLabel>
      {/*
        Rolls rather than jumps, on the same grammar as the cart. On these steps
        the sidebar is not rendered at all, so this number is the customer's
        only view of what their choices are worth — a figure that silently swaps
        is a change they never see happen.
      */}
      <Amount>
        <AnimatedCents cents={dollarsToCents(Number(subTotal) || 0)} />
      </Amount>
      {discountCents > 0 ? (
        <Saved>You save {formatCents(discountCents)}</Saved>
      ) : null}
    </Price>
  );

  return (
    <>
      {canOpenSummary && (
        <SummaryScrim open={summaryOpen} onClose={closeSummary} />
      )}
      <Footer ref={footerRef}>
        {canOpenSummary && (
          <SummarySheet
            open={summaryOpen}
            onClose={closeSummary}
            labelledBy={triggerId}
            id={sheetId}
          />
        )}
        <FooterContent>
          {/*
            Three tracks, and the side tracks exist whether or not anything is
            in them. Step one has no Back button, and under the old
            `space-between` row that moved the price to a different place than
            every later step showed it — the one number the customer is
            tracking, jumping as they walk the funnel.
          */}
          <Side>
            {currentPage > 1 && (
              <BackButton onClick={prevAction}>{prevText}</BackButton>
            )}
          </Side>

          <Centre>
            {hidePrice ? null : canOpenSummary ? (
              <PriceTrigger
                id={triggerId}
                type="button"
                aria-expanded={summaryOpen}
                aria-controls={sheetId}
                onClick={() => setSummaryOpen((wasOpen) => !wasOpen)}
              >
                {priceBlock}
              </PriceTrigger>
            ) : (
              priceBlock
            )}
          </Centre>

          <SideEnd>
            <NextButton
              disabled={disabled || loading}
              loading={loading}
              type="primary"
              onClick={nextAction}
            >
              {nextText}
            </NextButton>
          </SideEnd>
        </FooterContent>
      </Footer>
    </>
  );
}

const defaultProps = {
  nextAction: () => {},
  prevAction: () => {},
  nextText: "Next",
  prevText: "Back",
  loading: false,
};

MobileNavbar.defaultProps = defaultProps;

/*
  White, not the old `#d3f7ff`. That cyan matched nothing else in the funnel —
  the cards are white, the trays `#f2f4f9`, the accent indigo — so the bar read
  as bolted on from an older design. The hard `0 -1px 5px rgba(0,0,0,.2)` drop
  shadow went with it, for a hairline and a soft lift.
*/
const Footer = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background: #fff;
  border-top: 1px solid #e8eaee;
  padding: 0.5rem 1rem calc(0.5rem + env(safe-area-inset-bottom, 0px));
  z-index: 3;
  box-shadow: 0 -6px 20px rgba(20, 24, 36, 0.06);
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const Side = styled.span`
  display: flex;
  justify-content: flex-start;
  min-width: 0;
`;

const SideEnd = styled(Side)`
  justify-content: flex-end;
`;

const Centre = styled.span`
  display: flex;
  justify-content: center;
  min-width: 0;
`;

const NextButton = styled(Button)`
  background-color: var(--blue-300, #323593);
  color: #fff;
  height: auto;
  min-height: 44px;
  padding: 0.5rem 1.5rem;
  border: none;
  text-align: center;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  width: auto;

  &:hover,
  &:focus {
    background-color: #282a7a;
    color: #fff;
  }

  .ant-btn-loading {
    padding-left: 1rem;
  }
`;

/*
  Demoted to a quiet text control: Back is used on some steps, Next on every
  one, and two buttons of equal weight made the customer choose between them.
  It keeps a full 44px target and sits in its own grid track, 12px clear of the
  price and a long way from Next.
*/
const BackButton = styled.button`
  min-height: 44px;
  padding: 0 10px;
  border: none;
  background: none;
  color: #6b7280;
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    color: #1f2430;
    background: #f4f5f8;
  }
`;

const PriceTrigger = styled.button`
  border: none;
  background: none;
  padding: 2px 6px;
  margin: 0;
  font-family: inherit;
  cursor: pointer;
  border-radius: 8px;
  min-width: 0;

  &:hover {
    background: #f4f5f8;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-300, #323593);
    outline-offset: 2px;
  }
`;

const Price = styled.span`
  display: block;
  text-align: center;
  min-width: 0;
`;

const PriceLabel = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.2;
  color: #6b7280;
`;

/*
  Near-black rather than indigo. The indigo is the filled Next button's now, and
  one accent worn by two things at once is what made the old bar read as having
  two primary actions.
*/
const Amount = styled.span`
  display: block;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.1;
  color: #1f2430;
  font-variant-numeric: tabular-nums;
`;

const Saved = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
  color: ${SAVINGS.ink};
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 5px;
  vertical-align: 2px;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid var(--blue-300, #323593);
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
  transition: transform 160ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
